import { Check, Copy, RefreshCw } from "lucide-react";
import { useId, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { m } from "@/paraglide/messages";
import { generateLorem, type LoremType } from "../lib/lorem";

const selectClass =
  "h-10 w-full rounded-md border border-border bg-card px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";

export function LoremGenerator() {
  const uid = useId();
  const [type, setType] = useState<LoremType>("paragraphs");
  const [count, setCount] = useState("3");
  const [startWithLorem, setStartWithLorem] = useState(true);
  // Fixed initial seed → identical server + client render (no hydration
  // mismatch). Regenerate bumps the seed (a client-only interaction).
  const [seed, setSeed] = useState(1);
  const [copied, setCopied] = useState(false);

  const output = useMemo(
    () => generateLorem({ type, count: Number(count), startWithLorem }, seed),
    [type, count, startWithLorem, seed],
  );

  const copy = async () => {
    if (!output) return;
    try {
      await navigator.clipboard.writeText(output);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // clipboard unavailable — ignore
    }
  };

  return (
    <div className="space-y-6">
      <div className="space-y-4 rounded-2xl border border-border bg-card/60 p-6 sm:p-8">
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor={`${uid}-type`}>{m.li_type()}</Label>
            <select
              id={`${uid}-type`}
              className={selectClass}
              value={type}
              onChange={(e) => setType(e.target.value as LoremType)}
            >
              <option value="paragraphs">{m.li_type_paragraphs()}</option>
              <option value="sentences">{m.li_type_sentences()}</option>
              <option value="words">{m.li_type_words()}</option>
            </select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor={`${uid}-count`}>{m.li_count()}</Label>
            <Input
              id={`${uid}-count`}
              type="number"
              min={1}
              max={100}
              value={count}
              onChange={(e) => setCount(e.target.value)}
            />
          </div>
        </div>

        <label className="flex items-center gap-2.5 text-sm">
          <input
            type="checkbox"
            checked={startWithLorem}
            onChange={(e) => setStartWithLorem(e.target.checked)}
            className="h-4 w-4 accent-primary"
          />
          {m.li_start_with_lorem()}
        </label>

        <div className="flex gap-2">
          <Button
            type="button"
            onClick={() => setSeed((s) => s + 1)}
            className="gap-2"
          >
            <RefreshCw className="h-4 w-4" />
            {m.li_generate()}
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={copy}
            className="gap-2"
            disabled={!output}
          >
            {copied ? (
              <Check className="h-4 w-4 text-emerald-500" />
            ) : (
              <Copy className="h-4 w-4" />
            )}
            {copied ? m.li_copied() : m.li_copy()}
          </Button>
        </div>
      </div>

      <textarea
        readOnly
        data-testid="li-output"
        aria-label={m.li_output_label()}
        value={output}
        className="min-h-[16rem] w-full resize-y rounded-lg border border-border bg-background p-4 text-sm leading-relaxed"
      />
    </div>
  );
}
